import { CampaignType, AdvancedOptions, IntervalType } from '@dev/ee-api/ee-api-models';
import { LineChart, palette } from '@dev/ee-components';
import { forEach, isEmpty, map } from 'lodash-es';
import React, { useEffect } from 'react';
import styled from 'styled-components';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { parse } from 'query-string';
import { useParams } from 'react-router-dom';
import { safelyDecodeBase64Url } from '@dev/ee-tools';
import { ReportsState } from '@/store/reports/types';
import {
    AbSplitHeaderControlsInterface,
    CampaignReportGraph,
    CampaignsInterface,
    PickedCampaignInterface,
} from '@/store/campaigns/types';
import dayjs from '@/services/tools/dayjs';
import { LONG_DATE_FORMAT } from '@/services/constants';
import useMobile from '@/services/hooks/useMobile';
import { getDailyLogStatusSummary, GetLinkTrackingListStart } from '@/store/reports/actions';
import { GetCampaignReportsSummary } from '@/store/campaigns/actions';

import { getAccountAdvancedOptions } from '@/store/account/actions';
import { getAbSplitTemplateClicksAndOpens, isClickTrackingEnabled, isOpenTrackingEnabled } from '../../helperFunctions';

export const LineChartWrapper = styled.div`
    margin-top: 32px;
    position: relative;
`;

const sumGraphDataValues = (
    data: Record<string, any>[],
    isClicksTracked: boolean,
    isOpensTracked: boolean,
    isLinkTracked: boolean
): number => {
    let dataValues = 0;

    if (!isClicksTracked && !isOpensTracked && !isLinkTracked) {
        return dataValues;
    }

    forEach(data, (el): void => {
        dataValues += (isClicksTracked ? el.Clicked : 0) + (isOpensTracked ? el.Opened : 0);
    });

    return dataValues;
};

const useChartsData = (
    pickedCampaign: PickedCampaignInterface,
    abSplitHeaderControls: AbSplitHeaderControlsInterface,
    type: CampaignType
): Record<string, any>[] => {
    const dailyLogsStatusSummary = useSelector(getDailyLogStatusSummary, shallowEqual);
    const advancedOptions = useSelector(getAccountAdvancedOptions);
    const { displayMode, currentTemplate } = abSplitHeaderControls;
    const isAbSplit = type === CampaignType.AbSplit;
    let dailyData;

    if (isAbSplit) {
        dailyData = getAbSplitTemplateClicksAndOpens(
            pickedCampaign.clicksAndOpensChartData,
            currentTemplate,
            displayMode
        );
    } else {
        dailyData = dailyLogsStatusSummary;
    }

    if (!dailyData) {
        return [];
    }

    const isClicksTracked = isClickTrackingEnabled(pickedCampaign?.campaign, advancedOptions);
    const isOpensTracked = isOpenTrackingEnabled(pickedCampaign?.campaign, advancedOptions);

    return map(
        dailyData,
        (el): CampaignReportGraph => ({
            Date: dayjs(el?.Date).format(LONG_DATE_FORMAT),
            ...(isOpensTracked ? { Opened: isAbSplit ? el?.Opens : el?.Opened } : {}),
            ...(isClicksTracked ? { Clicked: isAbSplit ? el?.Click : el?.Clicked } : {}),
        })
    );
};

export const Chart = (props: {
    type: CampaignType;
    AdvancedOptions: AdvancedOptions;
    isOpensTracked: boolean;
    isClicksTracked: boolean;
    isLinkTracked: boolean;
    reports: ReportsState;
    campaigns: CampaignsInterface;
}): JSX.Element => {
    const dispatch = useDispatch();
    const isMobile = useMobile();
    const { name } = useParams<{ name: string; type: CampaignType }>();
    const decodedName = safelyDecodeBase64Url(name);
    const query = parse(location.search, { arrayFormat: 'comma' });
    const from = query.from
        ? dayjs(query.from as string).format(LONG_DATE_FORMAT)
        : dayjs().subtract(7, 'day').format(LONG_DATE_FORMAT);
    const to = query.to ? dayjs(query.to as string).format(LONG_DATE_FORMAT) : dayjs().format(LONG_DATE_FORMAT);

    const { type, isOpensTracked, isClicksTracked, reports, isLinkTracked, campaigns } = props;
    const { pickedCampaign, abSplitHeaderControls } = campaigns;

    const chartData = useChartsData(pickedCampaign, abSplitHeaderControls, type);
    const { reportsSummaryLoading } = reports;

    const hasAnyGraphData = sumGraphDataValues(chartData, isClicksTracked, isOpensTracked, isLinkTracked);
    const chartLineConfig = [
        ...(isOpensTracked ? [{ color: palette.indigo[6], dataKey: 'Opened' }] : []),
        ...(isClicksTracked ? [{ color: palette.teal[4], dataKey: 'Clicked' }] : []),
    ];

    const xAxisTickFormatter = (tick: string): string => {
        const format = pickedCampaign?.campaign?.Type === CampaignType.Automated ? 'DD-MM' : 'LT';
        return dayjs.utc(tick).local().format(format);
    };

    const lineChartTooltipHeaderFormatter = (tick: string): string => {
        const format = pickedCampaign?.campaign?.Type === CampaignType.Automated ? 'DD.MM.YYYY' : 'DD-MM LT';
        return dayjs.utc(tick).local().format(format);
    };

    useEffect(() => {
        if (pickedCampaign?.campaign?.FromAutomation && isEmpty(reports?.linkTrackingList)) {
            dispatch(
                GetLinkTrackingListStart({
                    channelName: pickedCampaign?.campaign?.Name,
                    activePage: 1,
                    perPage: 4,
                    isRequiredFullDateRange: false,
                })
            );
            dispatch(
                GetCampaignReportsSummary({
                    campaignName: decodedName,
                    type,
                    from,
                    to,
                    interval: IntervalType.Summary,
                })
            );
        }
    }, []);
    const desktopProps = { xMinTickGap: 15 };
    const mobileProps = { margin: { left: -25 }, padding: { left: 1, right: 5 } };

    return (
        <LineChartWrapper>
            <LineChart
                lineConfig={chartLineConfig}
                data={chartData}
                isEmpty={!hasAnyGraphData}
                height={490}
                xAxisGroupName="Date"
                xAxisTickFormatter={(tick) => xAxisTickFormatter(tick as string)}
                tooltipHeaderFormatter={(tick) => lineChartTooltipHeaderFormatter(tick as string)}
                showLegend={!isMobile}
                isMobile={isMobile}
                fullWidth
                isLoading={reportsSummaryLoading || campaigns.loading}
                yAllowDecimals={false}
                {...(isMobile ? mobileProps : desktopProps)}
            />
        </LineChartWrapper>
    );
};
